import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "./utils";

// shadcn/ui Bubble (https://ui.shadcn.com/docs/components/base/bubble), ported to Tailwind 3.
// The visible surface of a message. The variant styles the BubbleContent inside it.

function BubbleGroup({ className, ...props }: React.ComponentProps<"div">) {
    return <div data-slot="bubble-group" className={cn("flex flex-col gap-2 min-w-0", className)} {...props} />;
}

const bubbleVariants = cva(
    "group/bubble relative flex flex-col gap-1 data-[align=end]:self-end group-data-[align=end]/message:self-end w-fit min-w-0 max-w-[80%] data-[variant=ghost]:max-w-full",
    {
        variants: {
            variant: {
                default:
                    "[&>[data-slot=bubble-content]]:bg-primary [&>[data-slot=bubble-content]]:text-primary-foreground",
                secondary:
                    "[&>[data-slot=bubble-content]]:bg-secondary [&>[data-slot=bubble-content]]:text-secondary-foreground",
                muted: "[&>[data-slot=bubble-content]]:bg-muted [&>[data-slot=bubble-content]]:text-foreground",
                tinted: "[&>[data-slot=bubble-content]]:bg-indigo-50 [&>[data-slot=bubble-content]]:text-foreground dark:[&>[data-slot=bubble-content]]:bg-indigo-950/60",
                outline:
                    "[&>[data-slot=bubble-content]]:border-border [&>[data-slot=bubble-content]]:bg-background [&>[data-slot=bubble-content]]:text-foreground",
                ghost: "[&>[data-slot=bubble-content]]:bg-transparent [&>[data-slot=bubble-content]]:p-0 [&>[data-slot=bubble-content]]:rounded-none",
                destructive:
                    "[&>[data-slot=bubble-content]]:bg-destructive/10 [&>[data-slot=bubble-content]]:text-destructive dark:[&>[data-slot=bubble-content]]:bg-destructive/20",
            },
        },
        defaultVariants: { variant: "default" },
    }
);

function Bubble({
    variant = "default",
    align = "start",
    className,
    ...props
}: React.ComponentProps<"div"> & VariantProps<typeof bubbleVariants> & { align?: "start" | "end" }) {
    return (
        <div
            data-slot="bubble"
            data-variant={variant}
            data-align={align}
            className={cn(bubbleVariants({ variant }), className)}
            {...props}
        />
    );
}

function BubbleContent({ asChild = false, className, ...props }: React.ComponentProps<"div"> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : "div";
    return (
        <Comp
            data-slot="bubble-content"
            className={cn(
                "px-3 py-2 border border-transparent rounded-xl w-fit min-w-0 max-w-full overflow-hidden text-sm leading-relaxed break-words",
                className
            )}
            {...props}
        />
    );
}

const bubbleReactionsVariants = cva(
    "z-10 absolute flex justify-center items-center gap-1 bg-muted px-1.5 py-0.5 rounded-full ring-2 ring-card w-fit text-sm shrink-0",
    {
        variants: {
            side: { top: "top-0 -translate-y-3/4", bottom: "bottom-0 translate-y-3/4" },
            align: { start: "left-3", end: "right-3" },
        },
        defaultVariants: { side: "bottom", align: "end" },
    }
);

function BubbleReactions({
    side = "bottom",
    align = "end",
    className,
    ...props
}: React.ComponentProps<"div"> & { align?: "start" | "end"; side?: "top" | "bottom" }) {
    return (
        <div
            data-slot="bubble-reactions"
            data-align={align}
            data-side={side}
            className={cn(bubbleReactionsVariants({ side, align }), className)}
            {...props}
        />
    );
}

export { BubbleGroup, Bubble, BubbleContent, BubbleReactions, bubbleVariants };
